<template>
  <div class="relative">
    <div
      class="swipe-btn w-1/2 flex items-start left-0 border-2 border-transparent"
    >
      <slot name="left"> </slot>
    </div>
    <div
      class="swipe-btn w-1/2 flex items-end right-0 border-2 border-transparent"
    >
      <slot name="right"> </slot>
    </div>
    <div
      class="swipe-action-container"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
    >
      <div
        class="swipe-item-content w-full"
        :style="{ transform: `translateX(${offset}px)` }"
        @click="reset"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SwipeActions',
  props: {
    demoSwipe: { type: String, default: null },
    demoDelay: { type: Number, default: 1000 },
    demoInterval: { type: Number, default: 5000 },
    maxDemo: { type: Number, default: 3 },

    // 👇 NOUVEAU : distance minimale avant de commencer à swiper
    minSwipeDistance: {
      type: Number,
      default: 50,
    },
  },
  data() {
    return {
      offset: 0,
      startX: 0,
      startY: 0,
      dragging: false,
      mouseDown: false,
      hasStartedSwipe: false, // 👈 NOUVEAU
      demoTimer: null,
      demoIntervalTimer: null,
      demoActive: true,
    }
  },
  mounted() {
    if (this.demoSwipe) {
      this.demoTimer = setTimeout(() => {
        this.runDemoAnimation()
        this.demoIntervalTimer = setInterval(() => {
          if (this.demoActive) this.runDemoAnimation()
          if (this.nbDemo >= this.maxDemo) this.stopDemo()
        }, this.demoInterval)
      }, this.demoDelay)
    }
  },
  beforeUnmount() {
    this.stopDemo()
  },
  methods: {
    stopDemo() {
      this.demoActive = false
      clearTimeout(this.demoTimer)
      clearInterval(this.demoIntervalTimer)
    },

    runDemoAnimation() {
      if (!this.demoActive) return
      this.nbDemo++
      const delay = ms => new Promise(res => setTimeout(res, ms))

      const animate = async dir => {
        this.offset = dir === 'left' ? -80 : 80
        await delay(600)
        this.reset()
        await delay(400)
      }

      ;(async () => {
        if (this.demoSwipe === 'left') await animate('left')
        else if (this.demoSwipe === 'right') await animate('right')
        else if (this.demoSwipe === 'both') {
          await animate('left')
          await animate('right')
        }
      })()
    },

    /* --------------------------------
     * TOUCH LOGIC
     * -------------------------------- */
    onTouchStart(e) {
      if (e.touches.length !== 1) return
      this.stopDemo()
      this.dragging = true
      this.hasStartedSwipe = false // 👈 reset
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
    },

    onTouchMove(e) {
      if (!this.dragging) return
      const deltaX = e.touches[0].clientX - this.startX
      const deltaY = e.touches[0].clientY - this.startY

      // 👇 Empêche la détection tant que le X n'a pas dépassé le seuil
      if (!this.hasStartedSwipe) {
        if (Math.abs(deltaX) < this.minSwipeDistance) {
          this.offset = 0
          return
        }
        this.hasStartedSwipe = true
      }

      this.offset = Math.max(-120, Math.min(120, deltaX))
    },

    onTouchEnd() {
      if (!this.dragging) return
      this.handleAction()
      this.dragging = false
      this.hasStartedSwipe = false
    },

    /* --------------------------------
     * MOUSE LOGIC
     * -------------------------------- */
    onMouseDown(e) {
      this.stopDemo()
      this.mouseDown = true
      this.dragging = true
      this.hasStartedSwipe = false // 👈 reset
      this.startX = e.clientX
      this.startY = e.clientY
    },

    onMouseMove(e) {
      if (!this.dragging || !this.mouseDown) return

      const deltaX = e.clientX - this.startX
      const deltaY = e.clientY - this.startY

      // 👇 même logique : ne détecte pas avant seuil
      if (!this.hasStartedSwipe) {
        if (Math.abs(deltaX) < this.minSwipeDistance) {
          this.offset = 0
          return
        }
        this.hasStartedSwipe = true
      }

      this.offset = Math.max(-120, Math.min(120, deltaX))
    },

    onMouseUp() {
      if (!this.mouseDown) return
      this.handleAction()
      this.dragging = false
      this.mouseDown = false
      this.hasStartedSwipe = false
    },

    /* --------------------------------
     * ACTION LOGIC
     * -------------------------------- */
    handleAction() {
      if (!this.hasStartedSwipe) {
        this.reset()
        return
      }

      if (this.offset > 80) {
        this.offset = 80
        this.$emit('swipe', { sideVisible: 'left', reset: this.reset })
      } else if (this.offset < -80) {
        this.offset = -80
        this.$emit('swipe', { sideVisible: 'right', reset: this.reset })
      }

      this.reset()
    },

    reset() {
      this.offset = 0
    },
  },
}
</script>


<style scoped>
.swipe-action-container {
  position: relative;
  width: 100%;
  user-select: none;
}
.swipe-btn {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  color: #fff;
  font-weight: bold;
  outline: none;
  transition: background 0.2s;
}
.swipe-item-content {
  background: #fff;
  border-radius: 8px;
  min-height: 56px;
  display: flex;
  align-items: center;
  z-index: 2;
  position: relative;
  transition: transform 0.4s cubic-bezier(0.4, 2, 0.6, 1);
}
</style>
